import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { 
  Building2, 
  MapPin, 
  Phone, 
  Mail, 
  Users, 
  FileText, 
  Wrench, 
  Euro, 
  Plus
} from 'lucide-react';
import apiService from '../../services/apiService';
import toast from 'react-hot-toast';
import { formatCurrency, formatDate } from '../../utils/formatters';

interface CompanyDetails {
  _id: string;
  name: string;
  type: string;
  siret?: string;
  address: {
    street: string;
    city: string;
    postalCode: string;
    country: string;
  };
  contact: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    position?: string;
  };
  description?: string;
  website?: string;
  isActive: boolean;
  clientsCount: number;
  installationsCount: number;
  totalRevenue: number;
  lastInterventionDate?: string;
}

interface Request {
  _id: string;
  title: string;
  status: string;
  priority: string;
  createdAt: string;
}

interface Quote {
  _id: string;
  reference: string;
  title: string;
  status: string;
  totalAmount: number;
  createdAt: string;
}

interface Mission {
  _id: string;
  missionNumber: string;
  status: string;
  scheduledDate: string;
  report?: string;
}

interface Invoice {
  _id: string;
  invoiceNumber: string;
  status: string;
  totalAmount: number;
  issueDate: string;
}

const ViewCompany: React.FC = () => {
  const params = useParams<{ id: string }>();
  const companyId = (params as any)?.id || (params as any)?.companyId;
  const navigate = useNavigate();
  const [company, setCompany] = useState<CompanyDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'details' | 'requests' | 'quotes' | 'missions' | 'invoices'>('details');
  const [associatedRequests, setAssociatedRequests] = useState<Request[]>([]);
  const [associatedQuotes, setAssociatedQuotes] = useState<Quote[]>([]);
  const [associatedMissions, setAssociatedMissions] = useState<Mission[]>([]);
  const [associatedInvoices, setAssociatedInvoices] = useState<Invoice[]>([]);

  useEffect(() => {
    const fetchCompany = async () => {
      if (!companyId) return;
      try {
        setIsLoading(true);
        const companyData = await apiService.companies.getById(companyId);
        const apiCompany = 
          companyData?.data?.data?.company ??
          companyData?.data?.company ??
          companyData?.data?.data ??
          companyData?.data;

        if (!apiCompany) {
          throw new Error('Company not found in API response');
        }
        setCompany(apiCompany);
      } catch (error) {
        console.error('Error fetching company details:', error);
        toast.error('Erreur lors du chargement des détails de l\'entreprise.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchCompany();
  }, [companyId]);

  useEffect(() => {
    const fetchAssociatedData = async () => {
      if (!companyId) return;
      try {
        switch (activeTab) {
          case 'requests':
            const requestsRes = await apiService.requests.getAll({ companyId: companyId });
            setAssociatedRequests(requestsRes.items);
            break;
          case 'quotes':
            const quotesRes = await apiService.quotes.getAll({ companyId: companyId });
            setAssociatedQuotes(quotesRes.items);
            break;
          case 'missions':
            const missionsRes = await apiService.missions.getAll({ companyId: companyId });
            setAssociatedMissions(missionsRes.items);
            break;
          case 'invoices':
            const invoicesRes = await apiService.invoices.getAll({ companyId: companyId });
            setAssociatedInvoices(invoicesRes.items);
            break;
          default:
            break;
        }
      } catch (error) {
        console.error(`Error fetching associated ${activeTab}:`, error);
        toast.error(`Erreur lors du chargement des ${activeTab} associés.`);
      }
    };
    fetchAssociatedData();
  }, [companyId, activeTab]);

  if (isLoading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-full">
          <p>Chargement des détails de l\'entreprise...</p>
        </div>
      </Layout>
    );
  }

  if (!company) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-full">
          <p>Entreprise non trouvée.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <Building2 className="w-8 h-8 mr-3 text-primary-600" />
          Détails de l\'entreprise: {company.name}
        </h1>

        <Card className="p-6 space-y-6">
          <div className="flex items-center space-x-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{company.name}</h2>
              <p className="text-gray-600">{company.description}</p>
              {company.siret && <p className="text-sm text-gray-500">SIRET: {company.siret}</p>}
              {company.website && <p className="text-sm text-gray-500">Site web: <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">{company.website}</a></p>}
              <div className="mt-2">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${ 
                  company.isActive ? 'bg-success-100 text-success-800' : 'bg-error-100 text-error-800'
                }`}>
                  {company.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-gray-800">Informations de contact</h3>
              {company.contact && (
                <>
                  <p className="flex items-center text-gray-700">
                    <Users className="w-5 h-5 mr-2 text-gray-500" /> {company.contact.firstName} {company.contact.lastName} ({company.contact.position})
                  </p>
                  <p className="flex items-center text-gray-700">
                    <Mail className="w-5 h-5 mr-2 text-gray-500" /> {company.contact.email}
                  </p>
                  <p className="flex items-center text-gray-700">
                    <Phone className="w-5 h-5 mr-2 text-gray-500" /> {company.contact.phone}
                  </p>
                </>
              )}
            </div>

            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-gray-800">Adresse</h3>
              <p className="flex items-center text-gray-700">
                <MapPin className="w-5 h-5 mr-2 text-gray-500" /> {company.address.street}, {company.address.postalCode} {company.address.city}, {company.address.country}
              </p>
            </div>
          </div>
        </Card>

        {/* Tabs for associated data */}
        <div className="mt-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('details')}
                className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${ 
                  activeTab === 'details'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Détails
              </button>
              <button
                onClick={() => setActiveTab('requests')}
                className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${ 
                  activeTab === 'requests'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Demandes ({associatedRequests.length})
              </button>
              <button
                onClick={() => setActiveTab('quotes')}
                className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${ 
                  activeTab === 'quotes'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Devis ({associatedQuotes.length})
              </button>
              <button
                onClick={() => setActiveTab('missions')}
                className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${ 
                  activeTab === 'missions'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Missions ({associatedMissions.length})
              </button>
              <button
                onClick={() => setActiveTab('invoices')}
                className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${ 
                  activeTab === 'invoices'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Factures ({associatedInvoices.length})
              </button>
            </nav>
          </div>

          <div className="mt-6">
            {activeTab === 'details' && (
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-gray-900">Statistiques de l\'entreprise</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="p-4">
                    <p className="text-sm text-gray-600">Clients Associés</p>
                    <p className="text-2xl font-bold text-gray-900">{company.clientsCount}</p>
                  </Card>
                  <Card className="p-4">
                    <p className="text-sm text-gray-600">Installations Réalisées</p>
                    <p className="text-2xl font-bold text-gray-900">{company.installationsCount}</p>
                  </Card>
                  <Card className="p-4">
                    <p className="text-sm text-gray-600">Revenu Total Généré</p>
                    <p className="text-2xl font-bold text-gray-900">{formatCurrency(company.totalRevenue)}</p>
                  </Card>
                </div>
              </div>
            )}

            {activeTab === 'requests' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">Demandes Associées</h3>
                  <Button size="sm" onClick={() => navigate(`/admin/requests/add?companyId=${company._id}`)}>
                    <Plus className="w-4 h-4 mr-2" /> Nouvelle Demande
                  </Button>
                </div>
                {associatedRequests.length === 0 ? (
                  <p className="text-gray-500">Aucune demande associée à cette entreprise.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {associatedRequests.map(request => (
                      <Card key={request._id} className="p-4 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/admin/requests/view/${request._id}`)}>
                        <h4 className="font-semibold text-gray-900">{request.title}</h4>
                        <p className="text-sm text-gray-600">Statut: {request.status}</p>
                        <p className="text-sm text-gray-600">Priorité: {request.priority}</p>
                        <p className="text-sm text-gray-600">Créée le: {formatDate(request.createdAt)}</p>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'quotes' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">Devis Associés</h3>
                  <Button size="sm" onClick={() => navigate(`/admin/quotes/add?companyId=${company._id}`)}>
                    <Plus className="w-4 h-4 mr-2" /> Nouveau Devis
                  </Button>
                </div>
                {associatedQuotes.length === 0 ? (
                  <p className="text-gray-500">Aucun devis associé à cette entreprise.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {associatedQuotes.map(quote => (
                      <Card key={quote._id} className="p-4 hover:bg-gray-50 cursor-pointer">
                        <h4 className="font-semibold text-gray-900">{quote.reference} - {quote.title}</h4>
                        <p className="text-sm text-gray-600">Statut: {quote.status}</p>
                        <p className="text-sm text-gray-600">Montant: {formatCurrency(quote.totalAmount)}</p>
                        <p className="text-sm text-gray-600">Créé le: {formatDate(quote.createdAt)}</p>
                        <div className="mt-2 flex space-x-2">
                          <Button variant="secondary" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/admin/quotes/view/${quote._id}`); }}>Voir</Button>
                          <Button variant="secondary" size="sm" onClick={(e) => { e.stopPropagation(); window.open(`/api/quotes/${quote._id}/pdf`, '_blank'); }}>PDF</Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'missions' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">Missions Associées</h3>
                  <Button size="sm" onClick={() => navigate(`/admin/add-mission?companyId=${company._id}`)}>
                    <Plus className="w-4 h-4 mr-2" /> Nouvelle Mission
                  </Button>
                </div>
                {associatedMissions.length === 0 ? (
                  <p className="text-gray-500">Aucune mission associée à cette entreprise.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {associatedMissions.map(mission => (
                      <Card key={mission._id} className="p-4 hover:bg-gray-50 cursor-pointer">
                        <h4 className="font-semibold text-gray-900">{mission.missionNumber}</h4>
                        <p className="text-sm text-gray-600">Statut: {mission.status}</p>
                        <p className="text-sm text-gray-600">Date prévue: {formatDate(mission.scheduledDate)}</p>
                        <div className="mt-2 flex space-x-2">
                          <Button variant="secondary" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/admin/missions/view/${mission._id}`); }}>Voir</Button>
                          {mission.report && (
                            <Button variant="secondary" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/admin/reports/view/${mission.report}`); }}>Rapport</Button>
                          )}
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'invoices' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">Factures Associées</h3>
                  <Button size="sm" onClick={() => navigate(`/admin/invoices/add?companyId=${company._id}`)}>
                    <Plus className="w-4 h-4 mr-2" /> Nouvelle Facture
                  </Button>
                </div>
                {associatedInvoices.length === 0 ? (
                  <p className="text-gray-500">Aucune facture associée à cette entreprise.</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {associatedInvoices.map(invoice => (
                      <Card key={invoice._id} className="p-4 hover:bg-gray-50 cursor-pointer">
                        <h4 className="font-semibold text-gray-900">{invoice.invoiceNumber}</h4>
                        <p className="text-sm text-gray-600">Statut: {invoice.status}</p>
                        <p className="text-sm text-gray-600">Montant: {formatCurrency(invoice.totalAmount)}</p>
                        <p className="text-sm text-gray-600">Date d\'émission: {formatDate(invoice.issueDate)}</p>
                        <div className="mt-2 flex space-x-2">
                          <Button variant="secondary" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/admin/invoices/view/${invoice._id}`); }}>Voir</Button>
                          <Button variant="secondary" size="sm" onClick={(e) => { e.stopPropagation(); window.open(`/api/invoices/${invoice._id}/pdf`, '_blank'); }}>PDF</Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ViewCompany;
